import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getCategoryDetail, deleteCategory, updateCategory } from '../../services/api';
import { 
  FiArrowLeft,
  FiEdit2,
  FiTrash2,
  FiPackage,
  FiFolder,
  FiLayers,
  FiUsers,
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiCalendar,
  FiHash,
  FiTag,
  FiEye,
  FiEyeOff,
  FiActivity,
  FiBarChart2,
  FiGrid,
  FiList,
  FiStar,
  FiShoppingBag,
  FiRefreshCw,
  FiAlertCircle,
  FiCheckCircle,
  FiCornerDownRight,
  FiChevronRight,
  FiChevronDown,
  FiClock,
  FiUser
} from 'react-icons/fi';

function CategoryDetail({ admin }) {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSubcategories, setExpandedSubcategories] = useState([]);
  const [statistics, setStatistics] = useState({
    growthRate: 0,
    popularSearches: [],
    peakHours: [],
    listingDistribution: []
  });

  useEffect(() => {
    fetchCategoryDetail();
  }, [categoryId]);

  const fetchCategoryDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getCategoryDetail(categoryId);
      if (response.success) {
        setCategory(response.data);
        // Simulate fetching additional analytics data
        fetchAnalyticsData(response.data);
      } else {
        throw new Error(response.message || 'Failed to load category details');
      }
    } catch (err) {
      setError('Failed to load category details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async (categoryData) => {
    // In a real app, this would be an API call
    setTimeout(() => {
      setStatistics({
        growthRate: 12.5,
        popularSearches: [
          { term: categoryData.name + ' for kids', count: 245 },
          { term: 'Best ' + categoryData.name, count: 189 },
          { term: categoryData.name + ' accessories', count: 156 },
          { term: 'Affordable ' + categoryData.name, count: 132 }
        ],
        peakHours: [
          { hour: '09:00', listings: 45 },
          { hour: '14:00', listings: 67 },
          { hour: '19:00', listings: 52 },
          { hour: '22:00', listings: 38 }
        ],
        listingDistribution: [
          { status: 'Active', count: categoryData.active_listings || 0, color: '#10B981' },
          { status: 'Pending', count: Math.floor((categoryData.total_listings || 0) * 0.1), color: '#F59E0B' },
          { status: 'Sold', count: Math.floor((categoryData.total_listings || 0) * 0.3), color: '#3B82F6' },
          { status: 'Expired', count: Math.floor((categoryData.total_listings || 0) * 0.1), color: '#6B7280' }
        ]
      });
    }, 500);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${category.name}"? This will also delete ${category.subcategory_count} subcategories. This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await deleteCategory(categoryId);
      if (response.success) {
        setSuccessMessage(`Category "${category.name}" deleted successfully!`);
        setTimeout(() => {
          navigate('/admin/categories');
        }, 1500);
      } else {
        throw new Error(response.message || 'Delete failed');
      }
    } catch (err) {
      setError('Failed to delete category: ' + err.message);
    }
  };

  const toggleSubcategoryView = (subcategoryId) => {
    setExpandedSubcategories(prev =>
      prev.includes(subcategoryId)
        ? prev.filter(id => id !== subcategoryId)
        : [...prev, subcategoryId]
    );
  };

  const handleStatusToggle = async () => {
    try {
      const response = await updateCategory(categoryId, {
        is_active: !category.is_active
      });
      if (response.success) {
        setCategory(prev => ({
          ...prev,
          is_active: !prev.is_active
        }));
        setSuccessMessage(`Category ${!category.is_active ? 'activated' : 'deactivated'} successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      setError('Failed to update category status: ' + err.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { color: 'bg-green-900/30', text: 'text-green-400', icon: <FiActivity className="mr-1" /> },
      inactive: { color: 'bg-red-900/30', text: 'text-red-400', icon: <FiEyeOff className="mr-1" /> },
      pending: { color: 'bg-yellow-900/30', text: 'text-yellow-400', icon: <FiClock className="mr-1" /> }
    };
    
    const config = statusMap[status] || statusMap.inactive;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color} ${config.text}`}>
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Loading Skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="mb-6">
            <div className="flex items-center space-x-4">
              <div className="h-10 w-10 bg-gray-700 rounded-lg"></div>
              <div>
                <div className="h-6 bg-gray-700 rounded w-48 mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-64"></div>
              </div>
            </div>
          </div>

          {/* Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="h-4 bg-gray-700 rounded w-24 mb-3"></div>
                <div className="h-8 bg-gray-700 rounded w-16"></div>
              </div>
            ))}
          </div>

          {/* Content Skeleton */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-700 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="text-center py-12">
          <div className="text-gray-600 text-6xl mb-4">📂</div>
          <h3 className="text-xl font-medium text-white mb-2">Category Not Found</h3>
          <p className="text-gray-400 mb-6">The category you're looking for doesn't exist or has been removed.</p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              <FiArrowLeft className="inline mr-2" />
              Go Back
            </button>
            <Link
              to="/admin/categories"
              className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium"
            >
              Browse Categories
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/categories"
              className="text-gray-400 hover:text-white transition-colors flex items-center"
            >
              <FiArrowLeft className="mr-2" />
              <span className="hidden md:inline">Back to Categories</span>
            </Link>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                <span className="text-[#ff660e]">{category.name}</span>
                <span className="text-gray-500 ml-2">#{category.category_id}</span>
              </h1>
              <p className="text-gray-400 mt-1">Category details, analytics, and management</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleStatusToggle}
              className={`px-4 py-2.5 rounded-lg font-medium flex items-center transition-all duration-200 ${
                category.is_active !== false
                  ? 'bg-green-900/30 hover:bg-green-900/50 text-green-400 hover:text-green-300 border border-green-800/50'
                  : 'bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 border border-red-800/50'
              }`}
            >
              {category.is_active !== false ? (
                <>
                  <FiEye className="mr-2" />
                  Active
                </>
              ) : (
                <>
                  <FiEyeOff className="mr-2" />
                  Inactive
                </>
              )}
            </button>
            <Link
              to={`/admin/categories/edit/${categoryId}`}
              className="px-4 py-2.5 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 hover:text-blue-300 rounded-lg font-medium flex items-center transition-all duration-200 border border-blue-800/50 hover:scale-[1.02]"
            >
              <FiEdit2 className="mr-2" />
              Edit Category
            </Link>
            {admin.role === 'admin' && (
              <button
                onClick={handleDelete}
                className="px-4 py-2.5 bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 rounded-lg font-medium flex items-center transition-all duration-200 border border-red-800/50 hover:scale-[1.02]"
              >
                <FiTrash2 className="mr-2" />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Category Header Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
          <div className="flex items-start space-x-6">
            <div 
              className="w-24 h-24 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ backgroundColor: category.background_color || '#374151' }}
            >
              <span 
                className="text-4xl"
                style={{ color: category.icon_color || '#9CA3AF' }}
              >
                {category.icon_name || '📁'}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center flex-wrap gap-3 mb-2">
                <h2 className="text-2xl font-bold text-white">{category.name}</h2>
                {getStatusBadge(category.is_active !== false ? 'active' : 'inactive')}
                <span className="text-sm text-gray-400 bg-gray-700 px-2 py-1 rounded">
                  {category.icon_library}
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-4 text-sm text-gray-400">
                  <span className="flex items-center">
                    <FiCalendar className="mr-1" />
                    Created: {formatDate(category.created_at)}
                  </span>
                  {category.updated_at && category.updated_at !== category.created_at && (
                    <span className="flex items-center">
                      <FiRefreshCw className="mr-1" />
                      Updated: {formatDate(category.updated_at)}
                    </span>
                  )}
                </div>
                
                {category.parent_name && (
                  <div className="flex items-center text-sm text-gray-400">
                    <FiCornerDownRight className="mr-2 text-gray-500" />
                    Parent Category: 
                    <Link 
                      to={`/admin/categories/${category.parent_id}`}
                      className="ml-2 text-[#ff660e] hover:text-orange-400 transition-colors"
                    >
                      {category.parent_name}
                    </Link>
                  </div>
                )}
              </div>
              
              {category.description && (
                <div className="mt-4 p-3 bg-gray-700/50 rounded-lg border border-gray-600">
                  <p className="text-gray-300 text-sm">{category.description}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <div className="bg-gray-700/30 rounded-lg p-4 text-center hover:bg-gray-700/50 transition-colors group">
              <div className="flex flex-col items-center">
                <FiPackage className="text-blue-400 text-2xl mb-2 group-hover:scale-110 transition-transform" />
                <div className="text-xl font-bold text-white group-hover:text-blue-300 transition-colors">
                  {category.total_listings || 0}
                </div>
                <div className="text-xs text-gray-400 mt-1">Total Listings</div>
              </div>
            </div>
            
            <div className="bg-gray-700/30 rounded-lg p-4 text-center hover:bg-gray-700/50 transition-colors group">
              <div className="flex flex-col items-center">
                <FiActivity className="text-green-400 text-2xl mb-2 group-hover:scale-110 transition-transform" />
                <div className="text-xl font-bold text-white group-hover:text-green-300 transition-colors">
                  {category.active_listings || 0}
                </div>
                <div className="text-xs text-gray-400 mt-1">Active</div>
              </div>
            </div>
            
            <div className="bg-gray-700/30 rounded-lg p-4 text-center hover:bg-gray-700/50 transition-colors group">
              <div className="flex flex-col items-center">
                <FiLayers className="text-purple-400 text-2xl mb-2 group-hover:scale-110 transition-transform" />
                <div className="text-xl font-bold text-white group-hover:text-purple-300 transition-colors">
                  {category.subcategory_count || 0}
                </div>
                <div className="text-xs text-gray-400 mt-1">Subcategories</div>
              </div>
            </div>
            
            <div className="bg-gray-700/30 rounded-lg p-4 text-center hover:bg-gray-700/50 transition-colors group">
              <div className="flex flex-col items-center">
                <FiUsers className="text-cyan-400 text-2xl mb-2 group-hover:scale-110 transition-transform" />
                <div className="text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">
                  {category.unique_sellers || 0}
                </div>
                <div className="text-xs text-gray-400 mt-1">Sellers</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6 overflow-hidden">
        <div className="border-b border-gray-700 px-6">
          <nav className="flex -mb-px space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: <FiGrid /> },
              { id: 'subcategories', label: 'Subcategories', icon: <FiLayers />, count: category.subcategory_count },
              { id: 'listings', label: 'Listings', icon: <FiPackage />, count: category.total_listings },
              { id: 'analytics', label: 'Analytics', icon: <FiBarChart2 /> }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 flex items-center text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#ff660e] text-[#ff660e]'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                }`}
              >
                {tab.icon}
                <span className="ml-2">{tab.label}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="ml-2 bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Category Information */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-6">
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                      <FiTag className="mr-2 text-[#ff660e]" />
                      Category Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { label: 'Category ID', value: `#${category.category_id}`, icon: <FiHash /> },
                        { label: 'Name', value: category.name, icon: <FiTag /> },
                        { label: 'Parent Category', value: category.parent_name || 'Main Category', icon: <FiCornerDownRight /> },
                        { label: 'Icon', value: category.icon_name, icon: <FiStar /> },
                        { label: 'Icon Library', value: category.icon_library, icon: <FiLayers /> },
                        { label: 'Created', value: formatDate(category.created_at), icon: <FiCalendar /> },
                        ...(category.updated_at && category.updated_at !== category.created_at ? [
                          { label: 'Last Updated', value: formatDate(category.updated_at), icon: <FiRefreshCw /> }
                        ] : []),
                        { label: 'Status', value: category.is_active !== false ? 'Active' : 'Inactive', icon: <FiActivity /> }
                      ].map((item) => (
                        <div key={item.label} className="bg-gray-800/50 rounded-lg p-3">
                          <div className="flex items-center text-sm text-gray-400 mb-1">
                            {item.icon}
                            <span className="ml-2">{item.label}</span>
                          </div>
                          <div className="text-white font-medium">{item.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Appearance Preview */}
                  <div className="bg-gray-700/30 rounded-xl border border-gray-600 p-6">
                    <h3 className="text-lg font-medium text-white mb-6 flex items-center">
                      <FiEye className="mr-2 text-[#ff660e]" />
                      Appearance Preview
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-400 mb-3">Color Scheme</h4>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-300">Icon Color</span>
                            <div className="flex items-center space-x-3">
                              <div 
                                className="w-6 h-6 rounded border border-gray-600"
                                style={{ backgroundColor: category.icon_color }}
                              ></div>
                              <span className="text-sm font-mono text-gray-400">{category.icon_color}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-300">Background Color</span>
                            <div className="flex items-center space-x-3">
                              <div 
                                className="w-6 h-6 rounded border border-gray-600"
                                style={{ backgroundColor: category.background_color }}
                              ></div>
                              <span className="text-sm font-mono text-gray-400">{category.background_color}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-center justify-center">
                        <h4 className="text-sm font-medium text-gray-400 mb-4">Live Preview</h4>
                        <div 
                          className="w-24 h-24 rounded-2xl flex items-center justify-center shadow-xl mb-4"
                          style={{ backgroundColor: category.background_color }}
                        >
                          <span 
                            className="text-4xl"
                            style={{ color: category.icon_color }}
                          >
                            {category.icon_name}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 text-center">As seen by users</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Statistics Sidebar */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-600 p-6">
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                      <FiTrendingUp className="mr-2 text-green-400" />
                      Performance
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-400">Growth Rate</span>
                          <span className={`text-sm font-medium ${statistics.growthRate >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {statistics.growthRate >= 0 ? '+' : ''}{statistics.growthRate}%
                          </span>
                        </div>
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-green-500 to-green-400"
                            style={{ width: `${Math.min(100, Math.abs(statistics.growthRate) * 2)}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {category.avg_price && parseFloat(category.avg_price) > 0 && (
                        <div className="pt-4 border-t border-gray-600">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-400">Average Price</span>
                            <div className="flex items-center text-green-400">
                              <FiDollarSign className="mr-1" />
                              <span className="font-bold">{formatCurrency(category.avg_price)}</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Per listing in this category</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Popular Searches */}
                  <div className="bg-gray-800/50 rounded-xl border border-gray-600 p-6">
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                      <FiHash className="mr-2 text-[#ff660e]" />
                      Popular Searches
                    </h3>
                    <div className="space-y-3">
                      {statistics.popularSearches.map((search, index) => (
                        <div key={index} className="flex items-center justify-between hover:bg-gray-700/50 p-2 rounded-lg transition-colors">
                          <span className="text-sm text-gray-300 truncate">{search.term}</span>
                          <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                            {search.count} searches
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Subcategories Tab */}
          {activeTab === 'subcategories' && (
            <div>
              {category.subcategories && category.subcategories.length > 0 ? (
                <div className="bg-gray-700/30 rounded-xl border border-gray-600 overflow-hidden">
                  <div className="p-4 border-b border-gray-600">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-white">
                        {category.subcategories.length} Subcategor{category.subcategories.length === 1 ? 'y' : 'ies'}
                      </h3>
                      <Link
                        to={`/admin/categories/new?parent_id=${categoryId}`}
                        className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center hover:scale-[1.02] text-sm"
                      >
                        <FiLayers className="mr-2" />
                        Add Subcategory
                      </Link>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-600">
                    {category.subcategories.map((subcategory) => (
                      <div key={subcategory.category_id} className="p-4 hover:bg-gray-700/30 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div 
                              className="w-12 h-12 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: subcategory.background_color || '#374151' }}
                            >
                              <span 
                                className="text-xl"
                                style={{ color: subcategory.icon_color || '#9CA3AF' }}
                              >
                                {subcategory.icon_name || '📁'}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center space-x-3">
                                <h4 className="font-medium text-white">{subcategory.name}</h4>
                                <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                                  #{subcategory.category_id}
                                </span>
                              </div>
                              <div className="flex items-center space-x-4 mt-1 text-sm text-gray-400">
                                <span className="flex items-center">
                                  <FiPackage className="mr-1" />
                                  {subcategory.active_listings || 0} active
                                </span>
                                <span>•</span>
                                <span>{subcategory.total_listings || 0} total</span>
                                <span>•</span>
                                <span className="flex items-center">
                                  <FiCalendar className="mr-1" />
                                  {formatDate(subcategory.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <Link
                              to={`/admin/categories/${subcategory.category_id}`}
                              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center"
                            >
                              <FiEye className="mr-1" />
                              View
                            </Link>
                            <Link
                              to={`/admin/categories/edit/${subcategory.category_id}`}
                              className="px-3 py-1.5 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center"
                            >
                              <FiEdit2 className="mr-1" />
                              Edit
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-600 text-6xl mb-4">📂</div>
                  <h3 className="text-xl font-medium text-white mb-2">No Subcategories</h3>
                  <p className="text-gray-400 mb-6">This category doesn't have any subcategories yet.</p>
                  <Link
                    to={`/admin/categories/new?parent_id=${categoryId}`}
                    className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
                  >
                    <FiLayers className="mr-2" />
                    Create First Subcategory
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Listings Tab */}
          {activeTab === 'listings' && (
            <div>
              {category.recent_listings && category.recent_listings.length > 0 ? (
                <div className="bg-gray-700/30 rounded-xl border border-gray-600 overflow-hidden">
                  <div className="p-4 border-b border-gray-600">
                    <h3 className="text-lg font-medium text-white">Recent Listings</h3>
                    <p className="text-sm text-gray-400 mt-1">Latest listings in this category</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-600">
                      <thead className="bg-gray-800">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Listing
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Seller
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Created
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-600">
                        {category.recent_listings.map((listing) => (
                          <tr key={listing.listing_id} className="hover:bg-gray-700/30 transition-colors">
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-white">
                                {listing.title}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                #{listing.listing_id}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-white flex items-center">
                                <FiUser className="mr-2 text-gray-400" />
                                {listing.seller_first_name} {listing.seller_last_name}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                {listing.store_name}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-bold text-green-400">
                                {formatCurrency(listing.price)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                listing.status === 'active' 
                                  ? 'bg-green-900/30 text-green-400'
                                  : listing.status === 'sold'
                                  ? 'bg-blue-900/30 text-blue-400'
                                  : listing.status === 'pending'
                                  ? 'bg-yellow-900/30 text-yellow-400'
                                  : 'bg-gray-700 text-gray-400'
                              }`}>
                                {listing.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-400">
                              {formatDate(listing.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-600 text-6xl mb-4">🏷️</div>
                  <h3 className="text-xl font-medium text-white mb-2">No Listings Found</h3>
                  <p className="text-gray-400">This category doesn't have any listings yet.</p>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statistics.listingDistribution.map((item, index) => (
                  <div key={index} className="bg-gray-700/30 rounded-xl border border-gray-600 p-6 hover:border-gray-500 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">{item.status}</span>
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      ></div>
                    </div>
                    <div className="text-2xl font-bold text-white">{item.count}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {category.total_listings ? `${Math.round((item.count / category.total_listings) * 100)}% of total` : '0% of total'}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Peak Hours */}
                <div className="bg-gray-800/50 rounded-xl border border-gray-600 p-6">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                    <FiClock className="mr-2 text-[#ff660e]" />
                    Peak Listing Hours
                  </h3>
                  <div className="space-y-3">
                    {statistics.peakHours.map((hour, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm text-gray-300">{hour.hour}</span>
                        <div className="flex items-center space-x-3">
                          <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[#ff660e] to-orange-500"
                              style={{ width: `${(hour.listings / 67) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-400 w-10 text-right">
                            {hour.listings}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hierarchy */}
                <div className="bg-gray-800/50 rounded-xl border border-gray-600 p-6">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                    <FiLayers className="mr-2 text-purple-400" />
                    Category Hierarchy
                  </h3>
                  <div className="space-y-3">
                    {category.parent_name ? (
                      <div className="flex items-center space-x-3 text-gray-300">
                        <div className="p-2 bg-gray-700 rounded-lg">
                          <FiFolder className="text-purple-400" />
                        </div>
                        <div>
                          <div className="font-medium">{category.parent_name}</div>
                          <div className="text-xs text-gray-400">Parent Category</div>
                        </div>
                      </div>
                    ) : null}
                    
                    <div className="flex items-center space-x-3 text-white ml-4">
                      <FiChevronRight className="text-gray-500" />
                      <div className="p-2 bg-gradient-to-r from-[#ff660e]/20 to-orange-500/20 rounded-lg">
                        <FiFolder className="text-[#ff660e]" />
                      </div>
                      <div>
                        <div className="font-bold">{category.name}</div>
                        <div className="text-xs text-gray-400">Current Category</div>
                      </div>
                    </div>

                    {category.subcategory_count > 0 && (
                      <div className="flex items-center space-x-3 text-gray-300 ml-8">
                        <FiChevronRight className="text-gray-500" />
                        <div className="p-2 bg-gray-700 rounded-lg">
                          <FiLayers className="text-blue-400" />
                        </div>
                        <div>
                          <div className="font-medium">{category.subcategory_count} subcategories</div>
                          <div className="text-xs text-gray-400">Child categories</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default CategoryDetail;